// Caminho absoluto: src/components/CharacterPortrait/CharacterPortrait.tsx

import { useState } from 'react';
import { Landmark } from 'lucide-react';
import type { PersonagemFase } from '../../types/quiz.types';
import { buildRetratoUrl } from '../../utils/assetUrl';
import { getPersonagemPorFase, isFaseTribunal, TITULO_TRIBUNAL } from '../../services/personagemService';

export type PortraitVariant = 'compact' | 'inline' | 'portal' | 'hero';
export type PortraitMood = 'neutral' | 'success' | 'error';

interface Props {
  /** Fase da sessão. O personagem é resolvido pelo catálogo. */
  fase: number;
  /** Sobrescreve a resolução por fase (usado em telas fora do loop principal). */
  personagem?: PersonagemFase | null;
  variant?: PortraitVariant;
  mood?: PortraitMood;
  /** Exibe nome + categoria abaixo do retrato. `compact` nunca exibe. */
  showLabel?: boolean;
  /** Retrato above-the-fold: carrega com prioridade em vez de lazy. */
  eager?: boolean;
  className?: string;
}

/**
 * Larguras fluidas — mesma estratégia de clamp() da tipografia em index.css, mas
 * limitadas por `min(vw, dvh)`: só crescem quando o aparelho tem largura **e** altura
 * sobrando. Sem o termo em dvh, um telefone alto e estreito ganharia um retrato que
 * não cabe; sem o termo em vw, um tablet baixo e largo ganharia um retrato gigante.
 */
const SIZE: Record<PortraitVariant, string> = {
  compact: 'w-[clamp(32px,5dvh,44px)]',
  inline: 'w-[clamp(48px,min(15vw,9dvh),72px)]',
  portal: 'w-[clamp(56px,min(24vw,12dvh),112px)]',
  hero: 'w-[clamp(96px,min(30vw,16dvh),168px)]',
};

const MOOD: Record<PortraitMood, { frame: string; glow: string }> = {
  neutral: { frame: 'from-sky-400/70 to-sky-600/20', glow: 'rgba(14,165,233,.45)' },
  success: { frame: 'from-amber-400/80 to-amber-600/20', glow: 'rgba(245,158,11,.5)' },
  error: { frame: 'from-red-500/70 to-red-800/20', glow: 'rgba(239,68,68,.45)' },
};

const TRIBUNAL_THEME = { frame: 'from-purple-400/70 to-purple-700/20', glow: 'rgba(168,85,247,.45)' };

/**
 * Cache de falha em escopo de módulo. Sem ele, a silhueta pisca a cada transição
 * Pergunta → Portal de Erro → Pergunta quando a URL está indisponível, porque o
 * <img> é remontado e o onError dispara de novo.
 */
const urlsQueFalharam = new Set<string>();

function iniciais(nome: string): string {
  return nome
    .split(' ')
    .filter(Boolean)
    .map((parte) => parte[0])
    .slice(0, 2)
    .join('')
    .toUpperCase();
}

export function CharacterPortrait({
  fase,
  personagem: personagemProp,
  variant = 'inline',
  mood = 'neutral',
  showLabel = true,
  eager = false,
  className = '',
}: Props) {
  const tribunal = personagemProp == null && isFaseTribunal(fase);
  const personagem = personagemProp ?? getPersonagemPorFase(fase);

  const src = personagem ? buildRetratoUrl(personagem.imagem_slug) : null;
  // Guarda a URL que falhou (e não um booleano) para o estado não vazar
  // entre personagens quando o componente é reaproveitado em outra fase.
  const [srcComErro, setSrcComErro] = useState<string | null>(null);
  const erro = !src || srcComErro === src || urlsQueFalharam.has(src);

  // Sem personagem e sem Tribunal (fase fora do catálogo): não ocupa espaço.
  if (!personagem && !tribunal) return null;

  const tema = tribunal ? TRIBUNAL_THEME : MOOD[mood];
  const comLabel = showLabel && variant !== 'compact';
  const legenda = tribunal ? TITULO_TRIBUNAL : `${personagem!.nome} — Imortal da fase ${personagem!.fase}`;

  return (
    <div className={`flex flex-col items-center gap-1 shrink-0 ${className}`}>
      {/* wrapper do glow: drop-shadow respeita o clip-path (box-shadow não) */}
      <span
        className={`${SIZE[variant]} block [filter:drop-shadow(0_0_6px_var(--pglow))] motion-reduce:[filter:none]`}
        style={{ ['--pglow' as any]: tema.glow }}
      >
        {/* moldura chanfrada: gradiente + padding fazem as vezes de borda */}
        <span className={`block clip-hex-sm p-[2px] bg-gradient-to-br ${tema.frame}`}>
          {tribunal ? (
            <span
              className="flex aspect-square w-full items-center justify-center clip-hex-sm bg-slate-900"
              role="img"
              aria-label={comLabel ? undefined : legenda}
              aria-hidden={comLabel || undefined}
              title={legenda}
            >
              <Landmark className="w-1/2 h-1/2 text-purple-300" strokeWidth={1.5} />
            </span>
          ) : erro || !src ? (
            <span
              className="flex aspect-square w-full items-center justify-center clip-hex-sm bg-slate-800
                         font-cyber font-black text-slate-500 text-[clamp(0.7rem,4vw,1.15rem)] select-none"
              role="img"
              aria-label={comLabel ? undefined : legenda}
              aria-hidden={comLabel || undefined}
              title={legenda}
            >
              {iniciais(personagem!.nome)}
            </span>
          ) : (
            <img
              src={src}
              /* alt vazio quando o nome já está visível ao lado: evita leitura dupla */
              alt={comLabel ? '' : legenda}
              aria-hidden={comLabel || undefined}
              width={1080}
              height={1080}
              className="block w-full aspect-square object-cover object-[center_30%]
                         clip-hex-sm bg-slate-800 select-none pointer-events-none"
              loading={eager ? 'eager' : 'lazy'}
              fetchPriority={eager ? 'high' : 'auto'}
              decoding="async"
              draggable={false}
              onError={() => {
                urlsQueFalharam.add(src);
                setSrcComErro(src);
              }}
            />
          )}
        </span>
      </span>

      {comLabel && (
        // Sem `truncate`: nome de pessoa não se corta. Quebra em até 2 linhas.
        <span className="block text-center leading-tight max-w-[16ch]" title={legenda}>
          <span className="block font-cyber text-[10px] uppercase tracking-widest text-slate-300">
            {tribunal ? TITULO_TRIBUNAL : personagem!.nome}
          </span>
          {!tribunal && personagem!.categoria && (
            <span className="block text-[9px] uppercase tracking-wider text-sky-400/80 mt-0.5">
              {personagem!.categoria}
            </span>
          )}
        </span>
      )}
    </div>
  );
}
